import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { SEO } from '@/components/SEO'
import { Check, X, Calculator } from 'lucide-react'
import { plans } from '@/api/mockData'
// import { PurchaseButton } from '@/components/pricing/PurchaseButton'
import { PRICING_TIERS } from '@/lib/stripe-config'

function calculateBusinessPrice(pages: number) {
  if (pages < PRICING_TIERS.min || pages > PRICING_TIERS.max) {
    return 6000 // Default price if out of range
  }
  // The basePrice is $240.00 per page, so we multiply by the number of pages
  return Math.round(pages * (PRICING_TIERS.basePrice / 100)) // Convert from cents to dollars for display
}

export default function Pricing() {
  const [pages, setPages] = useState<number>(25)
  const [calculatedPrice, setCalculatedPrice] = useState<number>(calculateBusinessPrice(25))

  const handlePagesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value)
    if (!isNaN(value) && value >= PRICING_TIERS.min && value <= PRICING_TIERS.max) {
      setPages(value)
      setCalculatedPrice(calculateBusinessPrice(value))
    }
  }

  // Combine static and dynamic features for the Business Plan
  const businessFeatures = [
    ...plans.business.baseFeatures,
    `${pages} expert articles`,
    `${pages} semantic topics`,
  ]

  return (
    <>
      <SEO
        title="Pricing - WithWords"
        description="Choose the perfect WithWords plan for your semantic SEO needs. Simple, transparent pricing with no hidden fees."
      />
      <div className="py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <h1 className="text-4xl font-bold tracking-tight sm:text-6xl">
              Simple, transparent pricing
            </h1>
            <p className="mt-6 text-lg leading-8 text-muted-foreground">
              Choose the perfect plan to help you master semantic SEO and build topical authority.
              All plans include our core features with no hidden fees.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="isolate mx-auto mt-16 grid max-w-6xl grid-cols-1 gap-8 lg:grid-cols-2">
            {/* Pro Plan */}
            <div className="rounded-3xl p-8 ring-1 ring-gray-200 dark:ring-gray-800 xl:p-10 bg-card/50">
              <div className="flex items-center justify-between gap-x-4">
                <h2 className="text-lg font-semibold leading-8">{plans.pro.title}</h2>
              </div>
              <p className="mt-4 text-sm leading-6 text-muted-foreground">
                {plans.pro.description}
              </p>
              <p className="mt-6 flex items-baseline gap-x-1">
                <span className="text-4xl font-bold">{plans.pro.price}</span>
                <span className="text-sm font-semibold leading-6 text-muted-foreground">/month</span>
              </p>
              <Button
                asChild
                className="mt-6 w-full"
              >
                <Link to="https://app.withwords.app/signup/">
                  Get Started Free
                </Link>
              </Button>
              <ul role="list" className="mt-8 space-y-3 text-sm leading-6 text-muted-foreground">
                {plans.pro.features.map((feature) => (
                  <li key={feature} className="flex gap-x-3">
                    <Check className="h-6 w-5 flex-none text-primary" aria-hidden="true" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            {/* Business Plan */}
            <div className="rounded-3xl p-8 ring-2 ring-primary xl:p-10 bg-primary/5">
              <div className="flex items-center justify-between gap-x-4">
                <h2 className="text-lg font-semibold leading-8">{plans.business.title}</h2>
                <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold leading-5 text-primary">
                  Enterprise Ready
                </span>
              </div>
              <p className="mt-4 text-sm leading-6 text-muted-foreground">
                {plans.business.description}
              </p>

              {/* Price Calculator */}
              <div className="mt-6 p-4 bg-background/50 rounded-lg border border-primary/20">
                <div className="flex items-center gap-2 mb-4">
                  <Calculator className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold">Price Calculator</h3>
                </div>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="pages" className="block text-sm font-medium mb-1">
                      Number of Pages ({PRICING_TIERS.min}-{PRICING_TIERS.max})
                    </label>
                    <Input
                      id="pages"
                      type="number"
                      min={PRICING_TIERS.min}
                      max={PRICING_TIERS.max}
                      value={pages}
                      onChange={handlePagesChange}
                      className="max-w-[200px]"
                    />
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm font-medium">You'll receive:</p>
                    <ul className="text-sm space-y-1 text-muted-foreground">
                      <li>• {pages} topics in topical map</li>
                      <li>• {pages} content item briefs</li>
                      <li>• {pages} algorithmic authorship pages</li>
                    </ul>
                  </div>
                  <div className="pt-4 border-t border-primary/10">
                    <p className="text-lg font-semibold">
                      Estimated Price: <span className="text-primary">${calculatedPrice.toLocaleString()}</span>/month
                    </p>
                  </div>
                </div>
              </div>

              {/* Purchase button commented out due to checkout issues */}
              {/* <PurchaseButton
                quantity={pages}
                className="mt-6 w-full bg-primary text-primary-foreground hover:bg-primary/90"
              >
                Purchase Plan
              </PurchaseButton> */}

              <Button
                asChild
                className="mt-6 w-full bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <Link to="/contact">
                  Contact Sales
                </Link>
              </Button>

              <ul role="list" className="mt-8 space-y-3 text-sm leading-6 text-muted-foreground">
                {businessFeatures.map((feature) => (
                  <li key={feature} className="flex gap-x-3">
                    <Check className="h-6 w-5 flex-none text-primary" aria-hidden="true" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Feature Comparison Table */}
          <div className="mt-16">
            <h2 className="text-2xl font-bold text-center mb-8">Feature Comparison</h2>
            <div className="mx-auto max-w-4xl">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="py-4 text-left">Feature</th>
                    <th className="py-4 text-center">Pro</th>
                    <th className="py-4 text-center">Business</th>
                  </tr>
                </thead>
                <tbody>
                  {plans.features.map((feature) => (
                    <tr key={feature.name} className="border-b">
                      <td className="py-4">{feature.name}</td>
                      <td className="py-4 text-center">
                        {feature.pro ? (
                          <Check className="h-5 w-5 text-primary mx-auto" />
                        ) : (
                          <X className="h-5 w-5 text-muted-foreground mx-auto" />
                        )}
                      </td>
                      <td className="py-4 text-center">
                        {feature.business ? (
                          <Check className="h-5 w-5 text-primary mx-auto" />
                        ) : (
                          <X className="h-5 w-5 text-muted-foreground mx-auto" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mx-auto mt-16 max-w-2xl text-center">
            <h2 className="text-2xl font-bold">Need More Information?</h2>
            <p className="mt-6 text-lg leading-8 text-muted-foreground">
              Have questions about our pricing or need a custom solution?
              Our team is here to help you find the perfect plan for your needs.
            </p>
            <div className="mt-10 flex items-center justify-center gap-x-6">
              <Button variant="outline" size="lg" asChild>
                <Link to="/contact">Contact Sales</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
